import { useContext, useEffect, useState } from 'react';
import { Fade, Stack, InputAdornment, Box, OutlinedInput } from '@mui/material';
import { SearchIcon } from 'lucide-react';
import { roomDetailClasses, tableClasses, DetailAndDelBtn } from '../../utility';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';

const destinationsKeyTitleMap = [
  { key: 'license_number', title: 'License Number' },
  { key: 'facility_name', title: 'Facility Name' },
  { key: 'facility_type_name', title: 'Facility Type Name' },
  { key: 'facility_type', title: 'Facility Type' },
  { key: 'main_phone_number', title: 'Main Phone Number' },
  { key: 'mobile_phone_number', title: 'Mobile Phone Number' },
  { key: 'state', title: 'State' },
];

export default function _DestinationTab({ currDarkThemeClass, farmContextValue }) {
  const feedbackCX = useContext(FeedbackContext);

  const [selectedFarm, setSelectedFarm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (farmContextValue && Array.isArray(farmContextValue.farms) && farmContextValue.farms.length > 0) {
      const defaultFarm = farmContextValue.farms[0];
      setSelectedFarm(defaultFarm);
    }
  }, [farmContextValue]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 600);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  const formatData = items =>
    items.map((item, idx) => [`${idx + 1 + page * rowsPerPage}`.padStart(2, '0'), ...destinationsKeyTitleMap.map(({ key }) => item[key] ?? 'N/A')]);

  const fetchDestination = async () => {
    if (!selectedFarm) return;

    setLoading(true);
    try {
      let url = `transporters/destination?page=${page + 1}&limit=${rowsPerPage}`;

      if (debouncedSearch) {
        url += `&search=${encodeURIComponent(debouncedSearch)}`;
      }

      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const results = response.data.results || response.data || [];
        setData(formatData(results));
        setTotalCount(response.data.count ?? results.length ?? 0);
      }
    } catch (error) {
      console.error('Error fetching destinations', error);
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: 'Error fetching destinations.',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDestination();
  }, [selectedFarm, page, rowsPerPage, debouncedSearch]);

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  const metrcTagFilterWrapperProps = {
    direction: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
  };

  const handleSearch = event => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleKeyPress = event => {
    if (event.key === 'Enter') event.preventDefault();
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search by Make or Model',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon size={16} />
      </InputAdornment>
    ),
    onChange: handleSearch,
    onKeyPress: handleKeyPress,
    value: searchTerm,
  };

  const tableTitles = [{ title: 'Sl. No', width: 80 }, ...destinationsKeyTitleMap.map(e => ({ title: e.title }))];

  const displayProps = {
    animationClass: 'DestinationTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Destinations Found',
    entity: 'Destinations',
    tableEntries: data,
    tableTitles,
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack {...metrcTagFilterWrapperProps} padding={2}>
          <OutlinedInput {...outlinedInputProps} />
        </Stack>

        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...displayProps} />
        </Box>
      </Box>
    </Fade>
  );
}
